import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getList, deleteOne } from "../../dataProvider/API";

import { requestJson, buildApiUrl } from "../../dataProvider/requestJson";
import { ALERT_TEXT } from "../../constants/titles";

const initialState = {
  loading: false,
  governingBodies: {},
};

export const getGoverningBodies = createAsyncThunk(
  "teams/getGoverningBodies",
  ({ endpoint }) => getList(endpoint),
);

export const deleteTeam = createAsyncThunk("teams/deleteTeam", (endpoint) =>
  deleteOne(endpoint),
);

/**
 * Create/update go through requestJson rather than the legacy dataProvider, for the same
 * reason Venues was migrated (see Venues/sliceReducer).
 *
 * fetchJson ignores response.ok and calls JSON.parse on every body, so it rejects on a
 * successful response whose body is empty or not JSON — and the caller in pages/Teams only
 * closes the modal when this thunk resolves. A team could therefore be created while the
 * form stayed open showing the generic "request has been failed".
 *
 * It matters here specifically because the backend persists the team first and only then
 * uploads the logo and adds the coach, so a failure in either step arrives as a plain-text
 * 500 *after* the team exists. requestJson surfaces that message instead of hiding it.
 */
export const createTeam = createAsyncThunk(
  "teams/create",
  async ({ endpoint, data }, { rejectWithValue }) => {
    try {
      return await requestJson(buildApiUrl(endpoint), {
        method: "POST",
        body: JSON.stringify(data),
      });
    } catch (error) {
      return rejectWithValue(error?.message || ALERT_TEXT.BAD_REQUEST);
    }
  },
);

export const updateTeam = createAsyncThunk(
  "teams/update",
  async ({ endpoint, data }, { rejectWithValue }) => {
    try {
      return await requestJson(buildApiUrl(endpoint), {
        method: "POST",
        body: JSON.stringify(data),
      });
    } catch (error) {
      return rejectWithValue(error?.message || ALERT_TEXT.BAD_REQUEST);
    }
  },
);

export const teamsSlice = createSlice({
  name: "teams",
  initialState,
  extraReducers: {
    [deleteTeam.pending]: (state) => {
      state.loading = true;
    },
    [deleteTeam.fulfilled]: (state) => {
      state.loading = false;
      toast.success(ALERT_TEXT.SUCCESS_DELETE_TEAM);
    },
    [deleteTeam.rejected]: (state) => {
      state.loading = false;
      toast.error(ALERT_TEXT.BAD_REQUEST);
    },
    [getGoverningBodies.fulfilled]: (state, action) => {
      state.governingBodies = action.payload;
    },
    [updateTeam.fulfilled]: () => {
      toast.success(ALERT_TEXT.SUCCESS_UPDATE_TEAM);
    },
    // action.payload carries the backend's own message (e.g. a failed logo upload).
    [updateTeam.rejected]: (state, action) => {
      toast.error(action.payload || ALERT_TEXT.BAD_REQUEST);
    },
    [createTeam.fulfilled]: () => {
      toast.success(ALERT_TEXT.SUCCESS_CREATE_TEAM);
    },
    [createTeam.rejected]: (state, action) => {
      toast.error(action.payload || ALERT_TEXT.BAD_REQUEST);
    },
  },
});

export default teamsSlice.reducer;
